import CountryName from "../../assets/country.json";
import dayjs from "dayjs";
import React, {useState} from "react";
import {Button, Typography, Row, Col} from "antd";
import {CSVLink} from "react-csv";
import TablePage from "../../pages/tablePage/TablePage";
import {buildUrl} from "../../shared/utils/net";
import CustomAreaChart from "./chart/CustomAreaChart";
import {internalApi} from "../../common/axios";
import CustomPieChart from "./chart/CustomPieChart";
import {unparse} from "papaparse";
import GoToUserInfoButton from "../../shared/components/buttons/GoToUserInfoButton";
import toast from "react-hot-toast";

const UserStatistics = () => {
    const [userLevelRows, setUserLevelRows] = useState([]);
    const [userPaidRows, setUserPaidRows] = useState([]);
    const [userCountByCountryRows, setUserCountByCountryRows] = useState([]);
    const [userCountByOS, setUserCountByOS] = useState([]);

    const refreshRef = React.useRef();
    const platformList = ['UNKNOWN', 'ANDROID', 'IOS', 'WINDOWS'];

    const allUserColumns = [
        {
            title: '이름',
            dataIndex: 'name',
        },
        {
            title: 'PID',
            dataIndex: 'pid',
        },
        {
            title: '마켓',
            dataIndex: 'market',
        },
        {
            title: '가격($)',
            dataIndex: 'total_price',
            render: (text) => {
                return `$${text?.toFixed(2)}`;
            },
            sorter: (a, b) => a.total_price - b.total_price
        },
        {
            title: '레벨',
            dataIndex: 'lv',
            sorter: (a, b) => a.lv - b.lv
        },
        {
            title: '계정 생성일',
            dataIndex: 'created',
            render: (created) => {
                return dayjs(created).format('YYYY-MM-DD');
            },
            sorter: (a, b) => dayjs(a.created).unix() - dayjs(b.created).unix()
        },
        {
            title: '유저 정보',
            dataIndex: 'pid',
            render: (pid) => <GoToUserInfoButton pid={pid}/>,
        },
    ]
    const userCountBYCountryColumns = [
        {
            title: '국가',
            dataIndex: 'country',
            render: (country) => {
                return `${CountryName[country]?.name_kr || ''}(${country})`;
            }
        },
        {
            title: '유저 수',
            dataIndex: 'count',
            sorter: (a, b) => a.count - b.count
        },
        {
            title: '금일 접속 유저',
            dataIndex: 'todayLogin',
            sorter: (a, b) => a.todayLogin - b.todayLogin
        },
        {
            title: '잔존 유저 비율',
            render: (_, {count, todayLogin}) => {
                if (count === 0) return '0%';
                return `${((todayLogin / count) * 100).toFixed(2)}%`;
            },
            sorter: (a, b) => {
                const aRate = a.count === 0 ? 0 : a.todayLogin / a.count;
                const bRate = b.count === 0 ? 0 : b.todayLogin / b.count;
                return aRate - bRate;
            }
        }
    ]
    const userCountByOSColumns = [
        {
            title: 'OS',
            dataIndex: 'platform',
            render: (platform) => {
                return platformList[platform] ?? '합계';
            }
        },
        {
            title: '유저 수',
            dataIndex: 'count',
        }
    ]

    const userDataMap = [
        {
            title: '유저 레벨 랭킹',
            columns: allUserColumns,
            type: 'level',
            rows: userLevelRows,
            setRows: setUserLevelRows,
        },
        {
            title: '유저 결제 금액 랭킹',
            columns: allUserColumns,
            type: 'paid',
            rows: userPaidRows,
            setRows: setUserPaidRows,
        },
        {
            title: '국가 별 유저 수',
            columns: userCountBYCountryColumns,
            type: 'country',
            rows: userCountByCountryRows,
            setRows: setUserCountByCountryRows,
        },
        {
            title: 'OS 별 유저 수',
            columns: userCountByOSColumns,
            type: 'os',
            rows: userCountByOS,
            setRows: setUserCountByOS,
        },
    ]

    const downloadAllUserData = async () => {
        try {
            toast.loading("다운로드 중입니다. 잠시만 기다려 주세요.");
            const res = await internalApi.get(buildUrl('manager/analysis/ranking', {
                type: 'all_users',
                begin: 0,
                end: 0
            }));
            downloadCSV(res.data.rows); // CSV 다운로드 함수 호출
            toast.dismiss();
            toast.success("다운로드 완료");
        } catch (error) {
            console.error('Failed to fetch user data:', error);
        }
    }

    const downloadCSV = (data) => {
        const csv = '\ufeff' + unparse(data, {encoding: 'utf-8'}); // BOM 추가
        const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'all_user_data' + `_${dayjs().format('YYYYMMDDHHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div>
            <Button
                onClick={() => downloadAllUserData()}
                type="primary"
                size="small"
                style={{marginBottom: '10px'}}
            >
                전체 유저 데이터 다운로드
            </Button>
            {
                userDataMap.map((userData, index) => (
                    index % 2 === 0 && (
                        <Row gutter={16} key={index}>
                            <Col xs={24} sm={12}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography.Text type="secondary">
                                        {userDataMap[index].title}
                                    </Typography.Text>
                                    <CSVLink data={userDataMap[index].rows}
                                             filename={`'${userDataMap[index].type}'_${dayjs().format('YYYYMMDDHHmmss')}.csv`}
                                             style={{textDecoration: 'none'}}>
                                        <Button type="primary" size="small">
                                            Download
                                        </Button>
                                    </CSVLink>
                                </div>
                                <TablePage {...{
                                    columns: userDataMap[index].columns,
                                    refreshRef,
                                    url: buildUrl('manager/analysis/ranking', {
                                        type: userDataMap[index].type,
                                        begin: 0,
                                        end: 0
                                    }),
                                    setRows: userDataMap[index].setRows
                                }}/>
                            </Col>
                            {index + 1 < userDataMap.length && (
                                <Col xs={24} sm={12}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Typography.Text type="secondary">
                                            {userDataMap[index + 1].title}
                                        </Typography.Text>
                                        <CSVLink data={userDataMap[index + 1].rows}
                                                 filename={`'${userDataMap[index + 1].type}'_${dayjs().format('YYYYMMDDHHmmss')}.csv`}
                                                 style={{textDecoration: 'none'}}>
                                            <Button type="primary" size="small">
                                                Download
                                            </Button>
                                        </CSVLink>
                                    </div>
                                    <TablePage {...{
                                        columns: userDataMap[index + 1].columns,
                                        refreshRef,
                                        url: buildUrl('manager/analysis/ranking', {
                                            type: userDataMap[index + 1].type,
                                            begin: 0,
                                            end: 0
                                        }),
                                        setRows: userDataMap[index + 1].setRows
                                    }}/>
                                </Col>
                            )}
                        </Row>
                    )
                ))
            }
            <Row gutter={16} className="statistics-charts" style={{padding: 8}}>
                <Col flex={3}>
                    <CustomAreaChart title={'User Level'} aspect={4} type={'userLevel'}/>
                </Col>
                <Col flex={1}>
                    <CustomPieChart title={'User Level Rate'} type={'userLevel'}/>
                </Col>
            </Row>
        </div>
    );
}

export default UserStatistics;